/**
 * AnimationManager - Lightweight spritesheet/animation loader
 *
 * Loads TexturePacker-style JSON atlases alongside their PNG sheets
 * and exposes helpers to retrieve frame data at a given elapsed time.
 *
 * Designed to work for both Canvas2D and Pixi renderers.
 */
import { ClientLogger } from '../../logger/ClientLogger.js';

const animationLoggerFallback = (() => {
  if (typeof window !== 'undefined' && window.console) {
    return window.console;
  }
  return { log: () => {}, warn: () => {}, error: () => {}, info: () => {} };
})();
const animationLogger = ClientLogger.getLogger('renderer') || animationLoggerFallback;

const AnimationManager = {
  _atlases: new Map(), // animationPath -> atlas data
  _loadPromises: new Map(),
  _defaultFrameDuration: 100, // ms per frame fallback

  /**
   * Ensure an animation atlas is loaded.
   * @param {string} animationPath
   * @returns {Promise<void>}
   */
  async ensure(animationPath) {
    if (!animationPath) return;
    if (this._atlases.has(animationPath)) {
      return;
    }
    if (this._loadPromises.has(animationPath)) {
      return this._loadPromises.get(animationPath);
    }

    const promise = this._loadAtlas(animationPath)
      .catch((err) => {
        animationLogger.error(`[AnimationManager] Failed to load animation ${animationPath}`, err);
      })
      .finally(() => {
        this._loadPromises.delete(animationPath);
      });

    this._loadPromises.set(animationPath, promise);
    return promise;
  },

  /**
   * Get a specific frame for an animation given elapsed time.
   * Returns null until the atlas + texture are loaded.
   * @param {string} animationPath
   * @param {number} elapsedMs
   * @param {Object} options
   * @param {boolean} [options.loop=true]
   * @param {number} [options.frameDurationMs]
   * @param {number} [options.durationMs] - Total animation duration (used to derive per-frame time)
   * @param {string} [options.sequenceName] - Optional animation sequence name inside atlas
   * @returns {{image: HTMLImageElement, frame: Object, frameIndex: number, cacheKey: string}|null}
   */
  getFrame(animationPath, elapsedMs = 0, options = {}) {
    if (!animationPath) {
      return null;
    }

    const atlas = this._atlases.get(animationPath);

    if (!atlas || !atlas.ready) {
      // Kick off load if we haven't yet
      if (!this._loadPromises.has(animationPath)) {
        this.ensure(animationPath);
      }
      return null;
    }

    const {
      frames,
      image,
      sequences,
      frameOrder
    } = atlas;

    if (!frames || frames.length === 0 || !image || !image.complete) {
      return null;
    }

    const sequenceName = options.sequenceName && sequences[options.sequenceName]
      ? options.sequenceName
      : atlas.defaultSequence;

    const order = sequences[sequenceName] || frameOrder;
    if (!order || order.length === 0) {
      return null;
    }

    let frameDuration;
    if (options.durationMs && order.length > 0) {
      frameDuration = Math.max(1, options.durationMs / order.length);
    } else {
      frameDuration = Math.max(1, options.frameDurationMs || atlas.frameDuration || this._defaultFrameDuration);
    }
    const loop = options.loop !== false;
    const totalDuration = frameDuration * order.length;

    let frameIndex;
    if (!loop && elapsedMs >= totalDuration) {
      frameIndex = order.length - 1;
    } else {
      frameIndex = Math.floor((elapsedMs % totalDuration) / frameDuration);
      frameIndex = Math.min(order.length - 1, frameIndex);
    }

    const frameName = order[frameIndex];
    const frame = atlas.frameMap.get(frameName);
    if (!frame) {
      return null;
    }

    return {
      image,
      frame: frame.frameRect,
      sourceSize: frame.sourceSize,
      frameIndex,
      cacheKey: `${animationPath}|${frameName}`
    };
  },

  /**
   * Internal loader for an atlas JSON + image
   * @param {string} animationPath
   * @private
   */
  async _loadAtlas(animationPath) {
    const response = await fetch(animationPath);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    const data = await response.json();
    const imagePath = this._resolveImagePath(animationPath, data.meta?.image);

    const image = await this._loadImage(imagePath);

    const frames = [];
    const frameMap = new Map();
    const frameOrder = [];

    if (data.frames) {
      for (const [frameName, frameInfo] of Object.entries(data.frames)) {
        const frameRect = {
          x: frameInfo.frame?.x || 0,
          y: frameInfo.frame?.y || 0,
          w: frameInfo.frame?.w || frameInfo.sourceSize?.w || image.width,
          h: frameInfo.frame?.h || frameInfo.sourceSize?.h || image.height
        };
        const sourceSize = {
          w: frameInfo.sourceSize?.w || frameRect.w,
          h: frameInfo.sourceSize?.h || frameRect.h
        };
        const entry = { name: frameName, frameRect, sourceSize };
        frames.push(entry);
        frameMap.set(frameName, entry);
        frameOrder.push(frameName);
      }
    }

    const sequences = {};
    if (data.animations) {
      for (const [seqName, seqFrames] of Object.entries(data.animations)) {
        sequences[seqName] = Array.isArray(seqFrames) ? seqFrames.slice() : [];
      }
    }

    const atlas = {
      ready: true,
      frames,
      frameMap,
      frameOrder,
      sequences,
      defaultSequence: Object.keys(sequences)[0] || 'default',
      image,
      frameDuration: data.meta?.frameDuration ?? null
    };

    if (!atlas.sequences[atlas.defaultSequence]) {
      atlas.sequences[atlas.defaultSequence] = frameOrder.slice();
    }

    this._atlases.set(animationPath, atlas);
  },

  /**
   * Resolve atlas image path relative to the JSON path.
   * @private
   */
  _resolveImagePath(jsonPath, imageName) {
    if (!imageName) return jsonPath.replace(/\.json$/i, '.png');
    if (imageName.startsWith('http://') || imageName.startsWith('https://') || imageName.startsWith('/')) {
      return imageName;
    }
    const lastSlash = jsonPath.lastIndexOf('/');
    if (lastSlash === -1) {
      return imageName;
    }
    return jsonPath.slice(0, lastSlash + 1) + imageName;
  },

  /**
   * Load an Image object and resolve once ready.
   * @param {string} src
   * @private
   */
  _loadImage(src) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = (err) => {
        animationLogger.error(`[AnimationManager] Failed to load image ${src}`, err);
        reject(err);
      };
      img.src = src;
    });
  }
};

export { animationLoggerFallback, animationLogger, AnimationManager };
